import {UseFormRegister} from "react-hook-form/dist/types/form";
import {Control, Controller, UseFormGetValues, UseFormSetValue, UseFormWatch} from "react-hook-form";
import React, {useEffect} from "react";
import RadioInput from "../../components/RadioInput";
import DatePickerWrapper from "../../components/DataPickerWrapper";
import {FieldValues} from "react-hook-form/dist/types/fields";
import {FieldPath} from "react-hook-form/dist/types/path";
import {useIntervallo} from "@commons/hooks/useIntervallo";
import {PeriodoType} from "@commons/types";

interface RangeOptionsProps<TFieldValues extends FieldValues> {
    options?: PeriodoType[],
    enabled: boolean,
    register: UseFormRegister<TFieldValues>,
    getValues: UseFormGetValues<TFieldValues>,
    setValue: UseFormSetValue<TFieldValues>,
    watch: UseFormWatch<TFieldValues>,
    control: Control<TFieldValues, TFieldValues>,
    radioGroupName: FieldPath<TFieldValues>,
    dataDa: any,
    dataDaName: FieldPath<TFieldValues>,
    dataA: any,
    dataAName: FieldPath<TFieldValues>,
    addDaSempre?: boolean,
}

export function RangeOptions<TFieldValues extends FieldValues>(params: RangeOptionsProps<TFieldValues>) {
    const {
        options = ['today', 'm', 'b', 't', 'q', 's', 'cy', 'fy', 'custom', 'p'], // N.B. il default non include 'all'
        enabled,
        register,
        getValues,
        setValue,
        watch,
        control,
        radioGroupName,
        dataDa,
        dataDaName,
        dataA,
        dataAName,
        addDaSempre = false,
    } = params;
    // console.log('Aggiorno intervallo...')
    const format = 'yyyyLLdd';//'YYYYMMDD';
    const intervalloSelezionato: PeriodoType = watch(radioGroupName) as PeriodoType;
    const intervalloCustom = intervalloSelezionato === 'custom';

    const intervallo = useIntervallo(intervalloSelezionato, watch(dataDaName) as string, watch(dataAName) as string);

    // Aggiorno range del filtro al variare del periodo predefinito e dell'intervallo selezionato
    useEffect(() => {
        if (intervalloSelezionato !== 'custom') {
            if (!intervallo.valid) {
                // @ts-ignore
                setValue(dataDaName, '');
                // @ts-ignore
                setValue(dataAName, '');
            } else {
                // @ts-ignore
                setValue(dataDaName, intervallo.start);
                // @ts-ignore
                setValue(dataAName, intervallo.end);
            }
        }
    }, [intervalloSelezionato, setValue, dataDaName, dataAName, intervallo])

    return <div className="flex flex-col flex-wrap ml-4">
        <div className="w-full flex flex-row flex-wrap items-center">
            {options.includes('today') && <RadioInput value={'today'} disabled={!enabled} register={register} id={'oggi_' + radioGroupName} name={radioGroupName} label="oggi"
                                                      className="w-3/12 min-[1536px]:w-2/12 max-sm:basis-6/12"/>}
            {options.includes('m') && <RadioInput value={'m'} disabled={!enabled} register={register} id={'mese_' + radioGroupName} name={radioGroupName} label="mese"
                                                  className="w-3/12 min-[1536px]:w-2/12 max-sm:basis-6/12"/>}
            {options.includes('b') && <RadioInput value={'b'} disabled={!enabled} register={register} id={'bimestre_' + radioGroupName} name={radioGroupName} label="bimestre"
                                                  className="w-3/12 min-[1536px]:w-2/12 max-sm:basis-6/12"/>}
            {options.includes('t') && <RadioInput value={'t'} disabled={!enabled} register={register} id={'trimestre_' + radioGroupName} name={radioGroupName} label="trimestre"
                                                  className="w-3/12 min-[1536px]:w-2/12 max-sm:basis-6/12"/>}

            {options.includes('q') && <RadioInput value={'q'} disabled={!enabled} register={register} id={'quadrimestre_' + radioGroupName} name={radioGroupName} label="quadrimestre"
                                                  className="w-3/12 min-[1536px]:w-2/12 max-sm:basis-6/12"/>}
            {options.includes('s') && <RadioInput value={'s'} disabled={!enabled} register={register} id={'semestre_' + radioGroupName} name={radioGroupName} label="semestre"
                                                  className="w-3/12 min-[1536px]:w-2/12 max-sm:basis-6/12"/>}
            {options.includes('cy') && <RadioInput value={'cy'} disabled={!enabled} register={register} id={'CY_' + radioGroupName} name={radioGroupName} label="CY"
                                                   className="w-3/12 min-[1536px]:w-2/12 max-sm:basis-6/12"/>}
            {options.includes('fy') && <RadioInput value={'fy'} disabled={!enabled} register={register} id={'FY_' + radioGroupName} name={radioGroupName} label="FY"
                                                   className="w-3/12 min-[1536px]:w-2/12 max-sm:basis-6/12"/>}

            {options.includes('p') && <RadioInput value={'p'} disabled={!enabled} register={register} id={'pPred_' + radioGroupName} name={radioGroupName} label="periodo predefinito"
                                                  className="w-3/12 min-[1536px]:w-2/12 max-sm:basis-6/12"/>}
            <div className="w-9/12 max-sm:w-full min-[1536px]:w-6/12 container m-0">
                <div className="grid grid-cols-3 items-center">
                    {options.includes('custom') &&
                        <RadioInput value={'custom'} disabled={!enabled} register={register} id={'iCustom_' + radioGroupName} name={radioGroupName} label="intervallo custom"
                                    className="!w-full text-ellipsis"/>}
                    <Controller name={dataDaName}
                                control={control}
                                defaultValue={dataDa}
                                rules={{required: enabled && intervalloCustom}}
                                render={({field: {value, onChange}, fieldState, formState}) => {
                                    return <>{intervalloSelezionato !== 'all' &&
                                        <DatePickerWrapper enabled={enabled && intervalloCustom} fieldState={fieldState} value={value} onChange={onChange}/>}</>
                                }}
                    />
                    <Controller name={dataAName}
                                control={control}
                                defaultValue={dataA}
                                rules={{required: enabled && intervalloCustom}}
                                render={({field: {value, onChange}, fieldState, formState}) => {
                                    return <>{intervalloSelezionato !== 'all' &&
                                        <DatePickerWrapper enabled={enabled && intervalloCustom} fieldState={fieldState} value={value} onChange={onChange}/>}</>
                                }}
                    />
                </div>
            </div>
            {(addDaSempre || options.includes('all')) && <RadioInput value={'all'} disabled={!enabled} register={register} id={'ALL_' + radioGroupName} name={radioGroupName} label="tutti"
                                                                     className="w-3/12 min-[1536px]:w-2/12 max-sm:basis-6/12"/>}
        </div>

    </div>
}